import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";
import { Globals } from "../globals";
import { auth, db } from "../services/firebase";
import { collection, getDocs } from "firebase/firestore";
import SundayService, { sundayServiceConverter, ServiceType, SlotMode } from "../DTOs/SundayService";
import Preacher, { preacherConverter } from "../DTOs/Preacher";
import ChoirSong, { choirSongConverter } from "../DTOs/ChoirSong";
import Form from "react-bootstrap/esm/Form";
import Alert from "react-bootstrap/esm/Alert";
import Button from "react-bootstrap/esm/Button";
import Spinner from "react-bootstrap/esm/Spinner";
import Card from "react-bootstrap/esm/Card";
import { uploadItemToFirestore, updateItemInFirestore, getTodaySundayService } from "../services/firestoreService";
import { gemeindeSubPath } from "../services/gemeindePaths";
import I_IMC_Props from "../infos/I_IMC_Props";
import { useAuth } from "../hooks/useAuth";
import { loadAbsencesByPreacher } from "../services/absenceService";
import { AbsenceMap, findAbsence, formatAbsence } from "../utils/absences";
import { useGemeinde } from "../context/GemeindeContext";
import { filterPreachers, filterChoirSongs } from "../utils/autocompleteFilters";
import { DEFAULT_REPEAT_WARNING_WEEKS, findRepeat, formatRepeatDate } from "../utils/repeatWarning";
import { loadGemeindeSettings } from "../services/gemeindeSettingsService";
import { BIBLE_BOOKS, assembleBiblePassage, parseBiblePassage } from "../utils/bibleBooks";
import { choirModeFor, choirServiceTypesOf, DEFAULT_CHOIR_SERVICE_TYPES, inferServiceType, SELECTABLE_SERVICE_TYPES } from "../utils/serviceType";
import { logger } from "../utils/logger";
import BiblePassageInput from "./BiblePassageInput";

// Parses a YYYY-MM-DD input value into a local Date (so getDay() is timezone-stable).
function localDateFromInput(value: string): Date {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
}

export default function SimpleSundayServiceForm({ setVariant, setMessage, setShowMessage }: I_IMC_Props) {
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, loading: authLoading, canEdit } = useAuth();
    const { activeGemeindeId } = useGemeinde();
    const selectedDateFromState = location.state?.selectedDate as string | undefined;

    // Get service data from location state if editing
    const editingService = location.state?.service;
    const isEditing = !!editingService;

    // Set document title
    useEffect(() => {
        document.title = t("simpleSundayService.title");
    }, [t]);

    // Data for autocomplete
    const [preachers, setPreachers] = useState<Preacher[]>([]);
    const [absences, setAbsences] = useState<AbsenceMap>(new Map());
    const [choirSongs, setChoirSongs] = useState<ChoirSong[]>([]);
    // Gemeinde settings for the choir: how many song fields, and the repeat warning.
    const [choirSongCount, setChoirSongCount] = useState(4);
    // Service types the choir sings at (Gemeinde setting, GOD-49).
    const [choirTypes, setChoirTypes] = useState<ServiceType[]>(DEFAULT_CHOIR_SERVICE_TYPES);
    const [repeatWarningWeeks, setRepeatWarningWeeks] = useState(DEFAULT_REPEAT_WARNING_WEEKS);
    const [loadingData, setLoadingData] = useState(true);

    // Today's service loaded from Firestore (only used when not editing via list navigation)
    const [todayService, setTodayService] = useState<(SundayService & { id: string }) | null>(null);

    // Today's date in local timezone (YYYY-MM-DD)
    const todayDateStr = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    })();

    // Calculate next Sunday as default date
    const nextSundayDate = (() => {
        const date = new Date();
        const daysUntilSunday = date.getDay() === 0 ? 0 : 7 - date.getDay();
        date.setDate(date.getDate() + daysUntilSunday);
        return date.toISOString().split("T")[0];
    })();

    const initialDateStr = selectedDateFromState || (isEditing ? nextSundayDate : todayDateStr);

    // Form state
    const [serviceDate, setServiceDate] = useState(initialDateStr);
    const [serviceType, setServiceType] = useState<ServiceType>(() => inferServiceType(localDateFromInput(initialDateStr)));
    const [serviceLabel, setServiceLabel] = useState("");
    const serviceLabelRef = useRef<HTMLInputElement>(null);

    // sunday-afternoon: sermon vs prayer-concern mode
    const [afternoonMode, setAfternoonMode] = useState<"sermon" | "prayer">("sermon");

    // Prayer concerns: single (Mi/Sa/Sondergottesdienst) and list (Sonntag Nachmittag)
    const [prayerConcern, setPrayerConcern] = useState("");
    const [prayerConcerns, setPrayerConcerns] = useState<string[]>([""]);

    // weekday-special toggles
    const [hasChoirSongs, setHasChoirSongs] = useState(false);
    const [hasSermon, setHasSermon] = useState(true);
    const [hasSecondSermon, setHasSecondSermon] = useState(false);

    // Sermon 1
    const [selectedPreacher1, setSelectedPreacher1] = useState("");
    const [preacherInput1, setPreacherInput1] = useState("");
    const [selectedBook1, setSelectedBook1] = useState("");
    const [bookInput1, setBookInput1] = useState("");
    const [chapter1, setChapter1] = useState("");
    const [verses1, setVerses1] = useState("");
    const [topic1, setTopic1] = useState("");
    const [chapterError1, setChapterError1] = useState("");

    // Sermon 2
    const [selectedPreacher2, setSelectedPreacher2] = useState("");
    const [preacherInput2, setPreacherInput2] = useState("");
    const [selectedBook2, setSelectedBook2] = useState("");
    const [bookInput2, setBookInput2] = useState("");
    const [chapter2, setChapter2] = useState("");
    const [verses2, setVerses2] = useState("");
    const [topic2, setTopic2] = useState("");
    const [chapterError2, setChapterError2] = useState("");

    // Choir Songs (4 total)
    const [selectedChoirSong1, setSelectedChoirSong1] = useState("");
    const [choirSongInput1, setChoirSongInput1] = useState("");
    const [selectedChoirSong2, setSelectedChoirSong2] = useState("");
    const [choirSongInput2, setChoirSongInput2] = useState("");
    const [selectedChoirSong3, setSelectedChoirSong3] = useState("");
    const [choirSongInput3, setChoirSongInput3] = useState("");
    const [selectedChoirSong4, setSelectedChoirSong4] = useState("");
    const [choirSongInput4, setChoirSongInput4] = useState("");

    // Load preachers and choir songs
    useEffect(() => {
        // Don't load data while still checking auth
        if (authLoading) {
            return;
        }

        // If not authenticated, stop loading and don't fetch data
        if (!isAuthenticated) {
            setLoadingData(false);
            return;
        }

        async function fetchData() {
            if (!activeGemeindeId) {
                setLoadingData(false);
                return;
            }
            try {
                const preacherSnapshot = await getDocs(
                    collection(db, ...gemeindeSubPath(activeGemeindeId, Globals.PREACHER_SUBCOLLECTION)).withConverter(preacherConverter),
                );
                const preacherArray: Preacher[] = [];
                preacherSnapshot.forEach((doc) => {
                    preacherArray.push(doc.data());
                });
                setPreachers(preacherArray.sort((a, b) => a.surname.localeCompare(b.surname)));
                setAbsences(await loadAbsencesByPreacher(activeGemeindeId, preacherArray.map((p) => p.key)));

                const choirSongSnapshot = await getDocs(
                    collection(db, ...gemeindeSubPath(activeGemeindeId, Globals.CHOIRSONG_SUBCOLLECTION)).withConverter(choirSongConverter),
                );
                const choirSongArray: ChoirSong[] = [];
                choirSongSnapshot.forEach((doc) => {
                    // Archived songs are no longer suggested.
                    if (doc.data().archived) return;
                    choirSongArray.push(doc.data());
                });
                try {
                    const settings = await loadGemeindeSettings(activeGemeindeId);
                    setChoirTypes(choirServiceTypesOf(settings.choirServiceTypes));
                    if (typeof settings.choirSongCount === "number") {
                        setChoirSongCount(Math.min(4, Math.max(1, Math.floor(settings.choirSongCount))));
                    }
                    if (typeof settings.choirRepeatWarningWeeks === "number") {
                        setRepeatWarningWeeks(settings.choirRepeatWarningWeeks);
                    }
                } catch (_) {
                    // Only Gemeinde admins may read the settings; everyone else keeps the defaults.
                }
                // Songs are already unique by name in the new structure
                setChoirSongs(choirSongArray.sort((a, b) => a.name.toString().localeCompare(b.name.toString())));

                // When not editing via list navigation, load today's service if it exists
                if (!isEditing && typeof getTodaySundayService === "function") {
                    try {
                        const todaySvc = await getTodaySundayService(activeGemeindeId);
                        setTodayService(todaySvc);
                    } catch (err) {
                        // Silently ignore – show blank form if today's service can't be loaded
                        logger.error("ServiceForm.loadToday", err);
                    }
                }
            } catch (err) {
                setVariant("danger");
                setMessage(t("errors.loadingData"));
                setShowMessage(true);
            } finally {
                setLoadingData(false);
            }
        }
        fetchData();
    }, [setVariant, setMessage, setShowMessage, t, isAuthenticated, authLoading, isEditing, activeGemeindeId]);

    // Populate all form fields from an existing service (editing or today's auto-loaded service).
    const populateFromService = useCallback(
        (svc: SundayService) => {
            const d = new Date(svc.date);
            const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            setServiceDate(dateStr);

            // Legacy "manual" services use the Sonntag-Vormittag layout.
            const resolvedType: ServiceType =
                !svc.serviceType || svc.serviceType === "manual" ? "sunday-morning" : svc.serviceType;
            setServiceType(resolvedType);
            setServiceLabel(resolvedType === "weekday-special" ? svc.serviceLabel ?? "" : "");
            setAfternoonMode(svc.afternoonMode === "prayer" ? "prayer" : "sermon");
            setPrayerConcern(svc.prayerConcern ?? "");
            setPrayerConcerns(svc.prayerConcerns && svc.prayerConcerns.length > 0 ? [...svc.prayerConcerns] : [""]);

            setChoirSongInput1(svc.choirSong1Name);
            setSelectedChoirSong1(svc.choirSong1Name);
            setChoirSongInput2(svc.choirSong2Name);
            setSelectedChoirSong2(svc.choirSong2Name);
            setChoirSongInput3(svc.choirSong3Name);
            setSelectedChoirSong3(svc.choirSong3Name);
            setChoirSongInput4(svc.choirSong4Name);
            setSelectedChoirSong4(svc.choirSong4Name);

            // Any type: a service that has songs shows them (GOD-49).
            setHasChoirSongs(!!(svc.choirSong1Name || svc.choirSong2Name || svc.choirSong3Name || svc.choirSong4Name));
            if (resolvedType === "weekday-special") {
                setHasSermon(!!(svc.sermon1PreacherKey || svc.sermon1Topic || svc.sermon1BiblePassage));
                setHasSecondSermon(!!svc.sermon2PreacherKey);
            }

            const preacher1 = preachers.find((p) => p.key === svc.sermon1PreacherKey);
            if (preacher1) {
                setPreacherInput1(`${preacher1.name} ${preacher1.surname}`);
                setSelectedPreacher1(preacher1.key);
            }

            const parsed1 = parseBiblePassage(svc.sermon1BiblePassage || "");
            if (parsed1.book && BIBLE_BOOKS.includes(parsed1.book)) {
                setBookInput1(parsed1.book);
                setSelectedBook1(parsed1.book);
                setChapter1(parsed1.chapter);
                setVerses1(parsed1.verses);
            }
            setTopic1(svc.sermon1Topic);

            const preacher2 = preachers.find((p) => p.key === svc.sermon2PreacherKey);
            if (preacher2) {
                setPreacherInput2(`${preacher2.name} ${preacher2.surname}`);
                setSelectedPreacher2(preacher2.key);
            }

            const parsed2 = parseBiblePassage(svc.sermon2BiblePassage || "");
            if (parsed2.book && BIBLE_BOOKS.includes(parsed2.book)) {
                setBookInput2(parsed2.book);
                setSelectedBook2(parsed2.book);
                setChapter2(parsed2.chapter);
                setVerses2(parsed2.verses);
            }
            setTopic2(svc.sermon2Topic);
        },
        [preachers],
    );

    // Initialize form with editing data if provided
    useEffect(() => {
        if (isEditing && editingService && preachers.length > 0 && choirSongs.length > 0) {
            populateFromService(editingService);
        }
    }, [isEditing, editingService, preachers, choirSongs, populateFromService]);

    // Initialize form with today's service data when loaded from Firestore (not editing via list)
    useEffect(() => {
        if (!isEditing && todayService && preachers.length > 0) {
            populateFromService(todayService);
        }
    }, [isEditing, todayService, preachers, populateFromService]);

    // Handle preacher selection for sermon 1
    const handlePreacher1Select = (value: string) => {
        setPreacherInput1(value);
        const preacher = preachers.find((p) => `${p.name} ${p.surname}` === value);
        setSelectedPreacher1(preacher ? preacher.key : "");
    };

    // Handle preacher selection for sermon 2
    const handlePreacher2Select = (value: string) => {
        setPreacherInput2(value);
        const preacher = preachers.find((p) => `${p.name} ${p.surname}` === value);
        setSelectedPreacher2(preacher ? preacher.key : "");
    };

    // Handle choir song selections
    const handleChoirSong1Select = (value: string) => {
        setChoirSongInput1(value);
        const song = choirSongs.find((s) => s.name.toString() === value);
        setSelectedChoirSong1(song ? song.name.toString() : "");
    };

    const handleChoirSong2Select = (value: string) => {
        setChoirSongInput2(value);
        const song = choirSongs.find((s) => s.name.toString() === value);
        setSelectedChoirSong2(song ? song.name.toString() : "");
    };

    const handleChoirSong3Select = (value: string) => {
        setChoirSongInput3(value);
        const song = choirSongs.find((s) => s.name.toString() === value);
        setSelectedChoirSong3(song ? song.name.toString() : "");
    };

    const handleChoirSong4Select = (value: string) => {
        setChoirSongInput4(value);
        const song = choirSongs.find((s) => s.name.toString() === value);
        setSelectedChoirSong4(song ? song.name.toString() : "");
    };

    // Date change also suggests a service type (user can override afterwards).
    const handleDateChange = (value: string) => {
        setServiceDate(value);
        if (value) setServiceType(inferServiceType(localDateFromInput(value)));
    };

    // Prayer concern list handlers (sunday-afternoon prayer mode)
    const updatePrayerConcern = (index: number, value: string) => {
        setPrayerConcerns((prev) => prev.map((c, i) => (i === index ? value : c)));
    };
    const addPrayerConcern = () => setPrayerConcerns((prev) => [...prev, ""]);
    const removePrayerConcern = (index: number) =>
        setPrayerConcerns((prev) => (prev.length <= 1 ? [""] : prev.filter((_, i) => i !== index)));

    // Filter preachers based on input (returns top 10 matches)
    const filteredPreachers1 = useMemo(() => filterPreachers(preacherInput1, preachers), [preachers, preacherInput1]);
    const filteredPreachers2 = useMemo(() => filterPreachers(preacherInput2, preachers), [preachers, preacherInput2]);

    // Filter choir songs based on input (returns top 10 matches)
    const filteredChoirSongs1 = useMemo(() => filterChoirSongs(choirSongInput1, choirSongs), [choirSongs, choirSongInput1]);
    const filteredChoirSongs2 = useMemo(() => filterChoirSongs(choirSongInput2, choirSongs), [choirSongs, choirSongInput2]);
    const filteredChoirSongs3 = useMemo(() => filterChoirSongs(choirSongInput3, choirSongs), [choirSongs, choirSongInput3]);
    const filteredChoirSongs4 = useMemo(() => filterChoirSongs(choirSongInput4, choirSongs), [choirSongs, choirSongInput4]);

    // Check if sermon 2 fields are being filled (sunday-morning auto-requires the 4th choir song then)
    const isSermon2Filled = useMemo(() => {
        return !!(selectedPreacher2 || preacherInput2 || selectedBook2 || bookInput2 || chapter2 || verses2 || topic2);
    }, [selectedPreacher2, preacherInput2, selectedBook2, bookInput2, chapter2, verses2, topic2]);

    // Derived layout flags – "manual" renders like sunday-morning.
    const effectiveType: ServiceType = serviceType === "manual" ? "sunday-morning" : serviceType;
    const isSundayMorning = effectiveType === "sunday-morning";
    const isAfternoon = effectiveType === "sunday-afternoon";
    const isWednesday = effectiveType === "wednesday";
    const isSaturday = effectiveType === "saturday";
    const isSpecial = effectiveType === "weekday-special";
    // How choir songs are offered for this type at this Gemeinde (GOD-49).
    const choirMode = choirModeFor(effectiveType, choirTypes, hasChoirSongs);
    const sundayChoirShown = choirMode === "required" || hasChoirSongs;
    const choirRequired = choirMode === "required";

    const showSermon1 =
        isSundayMorning || isWednesday || (isAfternoon && afternoonMode === "sermon") || (isSpecial && hasSermon);
    // Saturday shows the Verkündiger sermon card (slot 2), so its chapter error must block too.
    const showSermon2 = isSundayMorning || isSaturday || (isSpecial && hasSermon && hasSecondSermon);

    // A chapter error only blocks submission when the relevant sermon is actually shown.
    const blockingChapterError = (showSermon1 && !!chapterError1) || (showSermon2 && !!chapterError2);

    // Focus the name field when switching to a Sondergottesdienst without a name yet.
    useEffect(() => {
        if (isSpecial && serviceLabelRef.current && !serviceLabel) {
            serviceLabelRef.current.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSpecial]);

    // The Gemeinde's number of choir songs decides which fields are offered. A
    // field beyond it stays visible while it holds a song, so nothing is hidden.
    const showChoirSlot = (slot: 1 | 2 | 3 | 4) =>
        slot <= choirSongCount || !!{ 1: choirSongInput1, 2: choirSongInput2, 3: choirSongInput3, 4: choirSongInput4 }[slot];

    function renderChoirToggle() {
        return (
            <Form.Check
                type="switch"
                id="hasChoirSongs"
                className="mb-2"
                label={t("serviceForm.hasChoirSongs")}
                checked={hasChoirSongs}
                onChange={(e) => setHasChoirSongs(e.target.checked)}
            />
        );
    }

    // Choir songs at a service type the Gemeinde sings at besides Sunday morning (GOD-49).
    function renderOptionalChoir() {
        return (
            <>
                <hr />
                <h4>{t("serviceForm.choirSongsOptional")}</h4>
                {([1, 2, 3, 4] as const).map((slot) => showChoirSlot(slot) && <div key={slot}>{renderChoirCard(slot, false)}</div>)}
            </>
        );
    }

    function showWarning(message: string) {
        setVariant("warning");
        setMessage(message);
        setShowMessage(true);
    }

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        const hasBiblePassage1 = !!(selectedBook1 && chapter1);
        const hasTopic1 = !!topic1.trim();
        const hasBiblePassage2 = !!(selectedBook2 && chapter2);
        const hasTopic2 = !!topic2.trim();

        // Sondergottesdienst: a name is required.
        if (isSpecial && !serviceLabel.trim()) {
            showWarning(t("serviceForm.serviceLabelRequired"));
            serviceLabelRef.current?.focus();
            return;
        }

        // Type-specific validation.
        if (isSundayMorning && choirRequired) {
            if (!selectedPreacher1) return showWarning(t("simpleSundayService.selectValidPreacher"));
            if (
                (showChoirSlot(1) && !selectedChoirSong1) ||
                (showChoirSlot(2) && !selectedChoirSong2) ||
                (showChoirSlot(3) && !selectedChoirSong3)
            )
                return showWarning(t("simpleSundayService.selectValidChoirSong"));
            if (!hasBiblePassage1 && !hasTopic1) return showWarning(t("sermon.bibleTextOrTopicRequired"));
            if (selectedBook1 && chapterError1) return showWarning(chapterError1);
            if (isSermon2Filled) {
                if (!selectedPreacher2) return showWarning(t("simpleSundayService.selectValidPreacher"));
                if (!hasBiblePassage2 && !hasTopic2) return showWarning(t("sermon.bibleTextOrTopicRequired"));
                if (selectedBook2 && chapterError2) return showWarning(chapterError2);
                if (showChoirSlot(4) && !selectedChoirSong4) return showWarning(t("simpleSundayService.selectValidChoirSong"));
            }
        } else if (isWednesday) {
            if (!selectedPreacher1) return showWarning(t("simpleSundayService.selectValidPreacher"));
            // Bibelstunde: a Bible passage is required.
            if (!hasBiblePassage1) return showWarning(t("sermon.bibleTextOrTopicRequired"));
            if (chapterError1) return showWarning(chapterError1);
        } else if (isAfternoon) {
            if (afternoonMode === "sermon") {
                if (!selectedPreacher1) return showWarning(t("simpleSundayService.selectValidPreacher"));
                if (!hasBiblePassage1 && !hasTopic1) return showWarning(t("sermon.bibleTextOrTopicRequired"));
                if (selectedBook1 && chapterError1) return showWarning(chapterError1);
            } else {
                const filled = prayerConcerns.map((c) => c.trim()).filter(Boolean);
                if (filled.length === 0) return showWarning(t("serviceForm.prayerConcernRequired"));
            }
        } else if (isSpecial && hasSermon) {
            if (!selectedPreacher1) return showWarning(t("simpleSundayService.selectValidPreacher"));
            if (!hasBiblePassage1 && !hasTopic1) return showWarning(t("sermon.bibleTextOrTopicRequired"));
            if (selectedBook1 && chapterError1) return showWarning(chapterError1);
            if (hasSecondSermon) {
                if (!selectedPreacher2) return showWarning(t("simpleSundayService.selectValidPreacher"));
                if (!hasBiblePassage2 && !hasTopic2) return showWarning(t("sermon.bibleTextOrTopicRequired"));
                if (selectedBook2 && chapterError2) return showWarning(chapterError2);
            }
        }
        // saturday (Wochenschluss): responsible person and prayer concern are both optional.

        setLoading(true);

        const form = e.currentTarget;
        const dateVal = form.Date.value;
        const serviceDateObject = new Date(dateVal);
        const weekday = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][serviceDateObject.getDay()] || "";

        const biblePassage1 = assembleBiblePassage(selectedBook1, chapter1, verses1);
        const biblePassage2 = assembleBiblePassage(selectedBook2, chapter2, verses2);

        // Build the persisted fields per service type. Anything not set for a type stays empty,
        // so switching type before saving never leaks stale data into the document.
        let s1Preacher = "";
        let s1Bible = "";
        let s1Topic = "";
        let s1Mode: SlotMode = "sermon";
        let s2Preacher = "";
        let s2Bible = "";
        let s2Topic = "";
        const s2Mode: SlotMode = "sermon";
        let cs1 = "";
        let cs2 = "";
        let cs3 = "";
        let cs4 = "";
        let finalServiceLabel = "";
        let finalPrayerConcern = "";
        let finalPrayerConcerns: string[] = [];
        let finalAfternoonMode: "sermon" | "prayer" | undefined;

        switch (effectiveType) {
            case "wednesday":
                s1Preacher = selectedPreacher1;
                s1Bible = biblePassage1;
                s1Mode = "sermon";
                finalPrayerConcern = prayerConcern.trim();
                break;
            case "saturday":
                // Slot 1 = Prediger that opens/closes with prayer (mode "intro" →
                // Gebetsanliegen); slot 2 = Verkündiger that holds the message (sermon).
                s1Preacher = selectedPreacher1;
                s1Mode = "intro";
                finalPrayerConcern = prayerConcern.trim();
                s2Preacher = selectedPreacher2;
                s2Bible = biblePassage2;
                s2Topic = topic2;
                break;
            case "sunday-afternoon":
                finalAfternoonMode = afternoonMode;
                if (afternoonMode === "sermon") {
                    s1Preacher = selectedPreacher1;
                    s1Bible = biblePassage1;
                    s1Topic = topic1;
                } else {
                    finalPrayerConcerns = prayerConcerns.map((c) => c.trim()).filter(Boolean);
                }
                break;
            case "weekday-special":
                finalServiceLabel = serviceLabel.trim();
                finalPrayerConcern = prayerConcern.trim();
                if (hasSermon) {
                    s1Preacher = selectedPreacher1;
                    s1Bible = biblePassage1;
                    s1Topic = topic1;
                    if (hasSecondSermon) {
                        s2Preacher = selectedPreacher2;
                        s2Bible = biblePassage2;
                        s2Topic = topic2;
                    }
                }
                break;
            default: // sunday-morning
                s1Preacher = selectedPreacher1;
                s1Bible = biblePassage1;
                s1Topic = topic1;
                s2Preacher = selectedPreacher2;
                s2Bible = biblePassage2;
                s2Topic = topic2;
                break;
        }

        // Choir songs are saved whenever the form shows their fields (GOD-49).
        const choirShown =
            choirMode === "required" || choirMode === "optional" || (choirMode === "toggle" && hasChoirSongs);
        if (choirShown) {
            cs1 = selectedChoirSong1;
            cs2 = selectedChoirSong2;
            cs3 = selectedChoirSong3;
            cs4 = selectedChoirSong4;
        }

        // Set numbers based on slot (1–4), keep 0 if no song is selected for that slot
        const choirSong1Number = cs1 ? 1 : 0;
        const choirSong2Number = cs2 ? 2 : 0;
        const choirSong3Number = cs3 ? 3 : 0;
        const choirSong4Number = cs4 ? 4 : 0;

        const sundayService = new SundayService(
            serviceDateObject,
            cs1,
            choirSong1Number,
            cs2,
            choirSong2Number,
            cs3,
            choirSong3Number,
            cs4,
            choirSong4Number,
            s1Preacher,
            s1Bible,
            s1Topic,
            s1Mode,
            s2Preacher,
            s2Bible,
            s2Topic,
            s2Mode,
            auth.currentUser!.uid,
            new Date(),
            auth.currentUser!.uid,
            new Date(),
            effectiveType,
            finalServiceLabel,
            weekday,
            undefined,
            undefined,
            finalPrayerConcern,
            finalPrayerConcerns,
            finalAfternoonMode,
        );

        try {
            // Determine whether to update an existing service or create a new one.
            // Only use the auto-loaded "today" service ID if the date field still matches today;
            // otherwise create a new document to avoid overwriting today's service with a different date.
            if (!activeGemeindeId) {
                setVariant("danger");
                setMessage(t("errors.anErrorOccurred"));
                setShowMessage(true);
                return;
            }
            let effectiveEditId: string | null = null;
            if (isEditing && editingService?.id) {
                effectiveEditId = editingService.id;
            } else if (!isEditing && todayService?.id && dateVal === todayDateStr) {
                effectiveEditId = todayService.id;
            }

            if (effectiveEditId) {
                // Update existing service
                await updateItemInFirestore<SundayService>(
                    activeGemeindeId,
                    Globals.SUNDAY_SERVICE_SUBCOLLECTION,
                    effectiveEditId,
                    sundayServiceConverter,
                    sundayService,
                );
                setVariant("success");
                setMessage(t("simpleSundayService.updated"));
                setShowMessage(true);
                // Navigate back to list
                navigate(Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST);
            } else {
                // Create new service
                await uploadItemToFirestore<SundayService>(
                    activeGemeindeId,
                    Globals.SUNDAY_SERVICE_SUBCOLLECTION,
                    sundayServiceConverter,
                    sundayService,
                );
                setVariant("success");
                setMessage(t("notifications.sundayServiceSavedSuccessfully"));
                setShowMessage(true);
                navigate(Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST);
            }
        } catch (e) {
            setVariant("danger");
            setMessage(String(e));
            setShowMessage(true);
        } finally {
            setLoading(false);
        }
    }

    // ---- Reusable field renderers ---------------------------------------------------------

    const renderPreacherField = (slot: 1 | 2, required: boolean, label?: string) => {
        const isOne = slot === 1;
        const value = isOne ? preacherInput1 : preacherInput2;
        const selected = isOne ? selectedPreacher1 : selectedPreacher2;
        const onSelect = isOne ? handlePreacher1Select : handlePreacher2Select;
        const filtered = isOne ? filteredPreachers1 : filteredPreachers2;
        const listId = isOne ? "preacherList1" : "preacherList2";
        return (
            <Form.Group className="mb-3">
                <Form.Label>{label ?? t("sermon.preacher")}</Form.Label>
                <Form.Control
                    type="text"
                    list={listId}
                    value={value}
                    onChange={(e) => onSelect(e.target.value)}
                    placeholder={t("simpleSundayService.selectPreacher") as string}
                    required={required}
                />
                {value && !selected && (
                    <Form.Text className="text-warning">{t("simpleSundayService.selectFromList")}</Form.Text>
                )}
                <datalist id={listId}>
                    {filtered.map((preacher) => (
                        <option key={preacher.key} value={`${preacher.name} ${preacher.surname}`} />
                    ))}
                </datalist>
            </Form.Group>
        );
    };

    const renderSermonCard = (slot: 1 | 2, opts: { preacherRequired: boolean; showTopic: boolean; bibleRequired?: boolean }) => {
        const isOne = slot === 1;
        const bookInput = isOne ? bookInput1 : bookInput2;
        const selectedBook = isOne ? selectedBook1 : selectedBook2;
        const chapter = isOne ? chapter1 : chapter2;
        const verses = isOne ? verses1 : verses2;
        const chapterError = isOne ? chapterError1 : chapterError2;
        const topic = isOne ? topic1 : topic2;
        const onBookChange = isOne
            ? (inp: string, bk: string) => {
                  setBookInput1(inp);
                  setSelectedBook1(bk);
              }
            : (inp: string, bk: string) => {
                  setBookInput2(inp);
                  setSelectedBook2(bk);
              };
        const onChapterChange = isOne ? setChapter1 : setChapter2;
        const onVersesChange = isOne ? setVerses1 : setVerses2;
        const onChapterErrorChange = isOne ? setChapterError1 : setChapterError2;
        const onTopicChange = isOne ? setTopic1 : setTopic2;
        return (
            <Card className="mb-3">
                <Card.Body>
                    {renderPreacherField(slot, opts.preacherRequired)}
                    <BiblePassageInput
                        bookInput={bookInput}
                        selectedBook={selectedBook}
                        chapter={chapter}
                        verses={verses}
                        chapterError={chapterError}
                        onBookChange={onBookChange}
                        onChapterChange={onChapterChange}
                        onVersesChange={onVersesChange}
                        onChapterErrorChange={onChapterErrorChange}
                        idSuffix={`-sermon${slot}`}
                    />
                    {opts.bibleRequired && (
                        <Form.Text className="text-muted d-block mb-2">
                            {t("simpleSundayService.biblePassage")} *
                        </Form.Text>
                    )}
                    {opts.showTopic && (
                        <Form.Group className="mb-3">
                            <Form.Label>{t("sermon.sermonTopic")}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={topic}
                                onChange={(e) => onTopicChange(e.target.value)}
                                placeholder={t("simpleSundayService.topicPlaceholder") as string}
                            />
                            <Form.Text className="text-muted">{t("sermon.optionalIfOtherFilled")}</Form.Text>
                        </Form.Group>
                    )}
                </Card.Body>
            </Card>
        );
    };

    const renderChoirCard = (slot: 1 | 2 | 3 | 4, required: boolean, optionalHelp = false) => {
        const cfg = {
            1: { title: t("choirSong.choirSong1"), value: choirSongInput1, onSelect: handleChoirSong1Select, selected: selectedChoirSong1, listId: "choirSongList1", filtered: filteredChoirSongs1 },
            2: { title: t("choirSong.choirSong2"), value: choirSongInput2, onSelect: handleChoirSong2Select, selected: selectedChoirSong2, listId: "choirSongList2", filtered: filteredChoirSongs2 },
            3: { title: t("choirSong.choirSong3"), value: choirSongInput3, onSelect: handleChoirSong3Select, selected: selectedChoirSong3, listId: "choirSongList3", filtered: filteredChoirSongs3 },
            4: { title: t("choirSong.choirSong4"), value: choirSongInput4, onSelect: handleChoirSong4Select, selected: selectedChoirSong4, listId: "choirSongList4", filtered: filteredChoirSongs4 },
        }[slot];
        // The same warning the conductor sees: sung, or planned, close to this service.
        const repeat = cfg.selected
            ? findRepeat(
                  serviceDate,
                  choirSongs.find((song) => song.name.toString() === cfg.selected)?.performanceDates ?? [],
                  repeatWarningWeeks
              )
            : null;
        return (
            <Card className="mb-3">
                <Card.Body>
                    <h5>{cfg.title}</h5>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("choirSong.choirSongNameLabel")}</Form.Label>
                        <Form.Control
                            type="text"
                            list={cfg.listId}
                            value={cfg.value}
                            onChange={(e) => cfg.onSelect(e.target.value)}
                            placeholder={t("simpleSundayService.selectChoirSong") as string}
                            required={required}
                        />
                        {cfg.value && !cfg.selected && (
                            <Form.Text className="text-warning">{t("simpleSundayService.selectFromList")}</Form.Text>
                        )}
                        {repeat && (
                            <Form.Text className="text-danger fw-semibold d-block">
                                {t(repeat.planned ? "simpleSundayService.repeatPlanned" : "simpleSundayService.repeatLast", {
                                    date: formatRepeatDate(repeat.date, serviceDate),
                                })}
                            </Form.Text>
                        )}
                        {optionalHelp && (
                            <Form.Text className="text-muted">{t("simpleSundayService.optionalIfOneSermon")}</Form.Text>
                        )}
                        <datalist id={cfg.listId}>
                            {cfg.filtered.map((song) => (
                                <option key={song.name.toString()} value={song.name.toString()} />
                            ))}
                        </datalist>
                    </Form.Group>
                </Card.Body>
            </Card>
        );
    };

    const renderPrayerSingle = () => (
        <Form.Group className="mb-3">
            <Form.Label>{t("serviceForm.prayerConcern")}</Form.Label>
            <Form.Control
                as="textarea"
                rows={2}
                value={prayerConcern}
                onChange={(e) => setPrayerConcern(e.target.value)}
                placeholder={t("serviceForm.prayerConcernPlaceholder") as string}
            />
        </Form.Group>
    );

    const renderPrayerList = () => (
        <>
            {prayerConcerns.map((concern, idx) => (
                <Form.Group className="mb-2" key={idx}>
                    <Form.Label>
                        {t("serviceForm.prayerConcern")} {idx + 1}
                    </Form.Label>
                    <div className="d-flex gap-2 align-items-start">
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={concern}
                            onChange={(e) => updatePrayerConcern(idx, e.target.value)}
                            placeholder={t("serviceForm.prayerConcernPlaceholder") as string}
                        />
                        {prayerConcerns.length > 1 && (
                            <Button type="button" variant="outline-danger" onClick={() => removePrayerConcern(idx)}>
                                {t("serviceForm.removePrayerConcern")}
                            </Button>
                        )}
                    </div>
                </Form.Group>
            ))}
            <Button type="button" variant="outline-secondary" size="sm" onClick={addPrayerConcern}>
                {t("serviceForm.addPrayerConcern")}
            </Button>
        </>
    );

    // Members may read services but not write them (the rules refuse it anyway) – say so
    // instead of offering a form that fails on save (GOD-53).
    if (!authLoading && isAuthenticated && !canEdit) {
        return (
            <div className="mx-auto" style={{ maxWidth: "680px", padding: "2rem 1.5rem 3rem" }} data-testid="form-no-permission">
                <Alert variant="info">
                    <Alert.Heading as="h2" className="h5">
                        {t("serviceDetail.noPermissionTitle")}
                    </Alert.Heading>
                    <p>{t("serviceDetail.noPermissionText")}</p>
                    <Button variant="primary" onClick={() => navigate(Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST)}>
                        {t("serviceDetail.toList")}
                    </Button>
                </Alert>
            </div>
        );
    }

    if (loadingData) {
        return (
            <div className="text-center my-5">
                <Spinner animation="border" />
                <p>{t("common.loading")}</p>
            </div>
        );
    }

    return (
        <>
            <div className="mx-auto" style={{ maxWidth: "680px", padding: "2rem 1.5rem 3rem" }}>
                <h1>{t("simpleSundayService.heading")}</h1>
                <br />
                <Form onSubmit={handleSubmit} id="SimpleSundayServiceForm">
                    <Form.Group className="mb-3" controlId="SimpleSundayServiceForm.Date">
                        <Form.Label>{t("simpleSundayService.serviceDate")}</Form.Label>
                        <Form.Control
                            type="date"
                            value={serviceDate}
                            onChange={(e) => handleDateChange(e.target.value)}
                            name="Date"
                            required
                        />
                    </Form.Group>

                    {Array.from(new Set([selectedPreacher1, selectedPreacher2])).map((key) => {
                        // Warn when a chosen preacher entered an absence for the service date.
                        const absence = findAbsence(absences, key, serviceDate);
                        const preacher = absence && preachers.find((p) => p.key === key);
                        if (!absence || !preacher) return null;
                        return (
                            <Alert variant="warning" key={key}>
                                {t("absences.formWarning", {
                                    name: `${preacher.name} ${preacher.surname}`,
                                    period: formatAbsence(absence, t("calendar.locale") as string),
                                })}
                                {absence.note && ` („${absence.note}“)`}
                            </Alert>
                        );
                    })}

                    <Form.Group className="mb-3" controlId="SimpleSundayServiceForm.ServiceType">
                        <Form.Label>{t("serviceForm.selectServiceType")}</Form.Label>
                        <Form.Select
                            value={serviceType === "manual" ? "sunday-morning" : serviceType}
                            onChange={(e) => setServiceType(e.target.value as ServiceType)}
                        >
                            {SELECTABLE_SERVICE_TYPES.map((st) => (
                                <option key={st} value={st}>
                                    {t(`serviceTypes.${st}`)}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {/* Sondergottesdienst: custom name */}
                    {isSpecial && (
                        <Form.Group className="mb-3" controlId="SimpleSundayServiceForm.ServiceLabel">
                            <Form.Label>
                                {t("serviceForm.serviceLabel")} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                ref={serviceLabelRef}
                                type="text"
                                value={serviceLabel}
                                onChange={(e) => setServiceLabel(e.target.value)}
                                placeholder={t("serviceForm.serviceLabelPlaceholder") as string}
                            />
                            <Form.Text className="text-muted">{t("serviceForm.serviceLabelHelp")}</Form.Text>
                        </Form.Group>
                    )}

                    {/* ---------------- Sonntag Vormittag (and legacy manual) ---------------- */}
                    {isSundayMorning && (
                        <>
                            <hr />
                            {choirMode === "toggle" && renderChoirToggle()}
                            {sundayChoirShown && <h4>{t("choirSong.choirSongsAtBeginning")}</h4>}
                            {sundayChoirShown && showChoirSlot(1) && renderChoirCard(1, choirRequired)}
                            {sundayChoirShown && showChoirSlot(2) && renderChoirCard(2, choirRequired)}

                            <hr />
                            <h4>{t("sermon.firstSermon")}</h4>
                            {renderSermonCard(1, { preacherRequired: true, showTopic: true })}

                            {sundayChoirShown && showChoirSlot(3) && (
                                <>
                                    <hr />
                                    <h4>{t("choirSong.anotherChoirSong")}</h4>
                                    {renderChoirCard(3, choirRequired)}
                                </>
                            )}

                            <hr />
                            <h4>{t("sermon.secondSermon")}</h4>
                            {renderSermonCard(2, { preacherRequired: false, showTopic: true })}

                            {sundayChoirShown && showChoirSlot(4) && (
                                <>
                                    <hr />
                                    <h4>{t("choirSong.anotherChoirSong")}</h4>
                                    {renderChoirCard(4, choirRequired && isSermon2Filled, !isSermon2Filled)}
                                </>
                            )}
                        </>
                    )}

                    {/* ---------------- Mittwoch – Bibelstunde ---------------- */}
                    {isWednesday && (
                        <>
                            <hr />
                            <h4>{t("serviceTypes.wednesday")}</h4>
                            {renderSermonCard(1, { preacherRequired: true, showTopic: false, bibleRequired: true })}
                            {renderPrayerSingle()}
                            {choirMode === "optional" && renderOptionalChoir()}
                        </>
                    )}

                    {/* ---------------- Samstag – Wochenschluss ---------------- */}
                    {isSaturday && (
                        <>
                            <hr />
                            <h4>{t("serviceTypes.saturday")}</h4>
                            <Card className="mb-3">
                                <Card.Body>{renderPreacherField(1, false, t("serviceForm.prayerResponsible") as string)}</Card.Body>
                            </Card>
                            {renderPrayerSingle()}

                            <hr />
                            <h5>{t("serviceForm.verkuendiger")}</h5>
                            {renderSermonCard(2, { preacherRequired: false, showTopic: true })}
                            {choirMode === "optional" && renderOptionalChoir()}
                        </>
                    )}

                    {/* ---------------- Sonntag Nachmittag ---------------- */}
                    {isAfternoon && (
                        <>
                            <hr />
                            <h4>{t("serviceTypes.sunday-afternoon")}</h4>
                            <Form.Group className="mb-3">
                                <Form.Label>{t("serviceForm.afternoonMode")}</Form.Label>
                                <div>
                                    <Form.Check
                                        inline
                                        type="radio"
                                        name="afternoonMode"
                                        id="afternoonMode-sermon"
                                        label={t("serviceForm.afternoonModeSermon")}
                                        checked={afternoonMode === "sermon"}
                                        onChange={() => setAfternoonMode("sermon")}
                                    />
                                    <Form.Check
                                        inline
                                        type="radio"
                                        name="afternoonMode"
                                        id="afternoonMode-prayer"
                                        label={t("serviceForm.afternoonModePrayer")}
                                        checked={afternoonMode === "prayer"}
                                        onChange={() => setAfternoonMode("prayer")}
                                    />
                                </div>
                            </Form.Group>
                            {afternoonMode === "sermon"
                                ? renderSermonCard(1, { preacherRequired: true, showTopic: true })
                                : renderPrayerList()}
                            {choirMode === "optional" && renderOptionalChoir()}
                        </>
                    )}

                    {/* ---------------- Sondergottesdienst ---------------- */}
                    {isSpecial && (
                        <>
                            <hr />
                            {renderChoirToggle()}
                            {hasChoirSongs && (
                                <>
                                    {([1, 2, 3, 4] as const).map(
                                        (slot) => showChoirSlot(slot) && <div key={slot}>{renderChoirCard(slot, false)}</div>
                                    )}
                                </>
                            )}

                            <hr />
                            <Form.Check
                                type="switch"
                                id="hasSermon"
                                className="mb-2"
                                label={t("serviceForm.hasSermon")}
                                checked={hasSermon}
                                onChange={(e) => setHasSermon(e.target.checked)}
                            />
                            {hasSermon && (
                                <>
                                    {renderSermonCard(1, { preacherRequired: true, showTopic: true })}
                                    <Form.Check
                                        type="checkbox"
                                        id="hasSecondSermon"
                                        className="mb-2"
                                        label={t("serviceForm.hasSecondSermon")}
                                        checked={hasSecondSermon}
                                        onChange={(e) => setHasSecondSermon(e.target.checked)}
                                    />
                                    {hasSecondSermon && renderSermonCard(2, { preacherRequired: true, showTopic: true })}
                                </>
                            )}

                            <hr />
                            {renderPrayerSingle()}
                        </>
                    )}

                    <br />
                    <Form.Group controlId="SimpleSundayServiceForm.Submit">
                        <Button
                            className="shadow-sm"
                            type="submit"
                            style={{ width: "100%" }}
                            disabled={loading || blockingChapterError}
                        >
                            {t("simpleSundayService.submitButton")}{" "}
                            {loading ? <Spinner size="sm" animation="grow" as="span" /> : ""}
                        </Button>
                    </Form.Group>

                    <div className="text-center mt-3">
                        <Button
                            variant="link"
                            onClick={() => navigate(Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST)}
                            disabled={loading}
                        >
                            {isEditing ? t("common.cancel") : t("simpleSundayService.viewList")}
                        </Button>
                    </div>
                </Form>
            </div>
        </>
    );
}
